import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { Suspense, useEffect } from "react";
import dynamic from "next/dynamic";
import { cn } from "@/lib/utils";
import type { AxiosError } from "axios";
import { Skeleton } from "./ui/skeleton";
import { useAtom } from "jotai";
import { migrationWizardOpenAtom, migrationWizardStepAtom } from "./migrationWizardState";
import { useActiveGithubOrgForUser, useIsPersonalOrg } from "@/hooks/useConnectedGithubOrgs";
import { BSErrorBoundary } from "./ui/bs-error-boundary";
import { usePostHog } from "posthog-js/react";
import { MIGRATION_WIZARD_EVENTS } from "@/lib/posthog-constants";
import { searchRepositories } from "@/lib/repositories";
import { useApi } from "@/hooks/useApi";
import { useQueryClient } from "react-query";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import Image from "next/image";
import MigrationWizardSvg from "@/assets/migration-wizard.svg";
import { useSidebar } from "./ui/sidebar";
function MigrationWizardSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col h-[300px] w-full gap-4"><Skeleton className="h-8 w-48 rounded-lg" /><Skeleton className="h-6 w-96 mb-4 rounded-lg" /><Skeleton className="w-full h-32 rounded-lg" /><Skeleton className="w-full h-12 rounded-lg" /><Skeleton className="w-full h-12 rounded-lg" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// DialogContent only mounts children while open, so the body chunk loads on first open.
const MigrationWizard = dynamic(() => import("./MigrationWizard").then(m => m.MigrationWizard), {
  loading: MigrationWizardSkeleton
});
function ErrorFallback(t0) {
  const $ = _c(2);
  const {
    error
  } = t0;
  if (error?.response?.status === 403) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DialogHeader><DialogTitle /><DialogDescription /></DialogHeader>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <>{t1}<div><div className="flex items-center justify-center h-[150px] w-full"><ExclamationCircleIcon className="w-12 h-12 text-red-500" /></div></div></>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  throw error;
}
export default function LazyMigrationWizard() {
  const [open, setOpen] = useAtom(migrationWizardOpenAtom);
  const [step, setStep] = useAtom(migrationWizardStepAtom);
  const posthog = usePostHog();
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const {
    isPersonalOrg
  } = useIsPersonalOrg();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();

  // Reset step to repository when dialog opens
  useEffect(() => {
    if (open) {
      setStep("repository");
    }
  }, [open, setStep]);

  // Allow closing on the first step (always) and on the PR step (so the
  // user can X out after PRs are created).
  const canClose = step === "repository" || step === "pull-request";
  return <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={0}>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <button type="button" aria-disabled={isPersonalOrg} className={cn(
          // Match the sidebar navigation item styling (not a "card").
          "group/migwiz mb-0 flex h-8 w-full items-center gap-2 rounded-sm p-1.5 text-left type-body-s-regular", "text-[#434546] hover:bg-[#0000000f] hover:text-[#161718]", "dark:text-[#bbb] dark:hover:bg-white/6 dark:hover:text-white",
          // Standard focus styles per design.md - only show border on focus-visible, not on regular focus
          "outline-hidden border border-transparent focus-visible:!border-2 focus-visible:!border-[var(--focus-border)] focus-visible:ring-0", "group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[state=collapsed]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8", isPersonalOrg ? "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-[#434546] dark:hover:bg-transparent dark:hover:text-[#bbb]" : "cursor-pointer")} onClick={e => {
            if (isPersonalOrg) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            posthog?.capture(MIGRATION_WIZARD_EVENTS.OPEN);
          }} onMouseEnter={() => {
            if (isPersonalOrg) return;
            void import("./MigrationWizard");
            // Eagerly fetch the initial page of repositories for the active org
            // to speed up user interactions with the dropdown in MigrationWizard.
            // Cache key must match the one used by `useRepositorySearch`.
            if (activeGithubOrg) {
              queryClient.prefetchQuery(["repositorySearch", activeGithubOrg, "", 100], () => searchRepositories({
                api,
                endpoint: `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories`,
                search: "",
                perPage: 100
              }), {
                staleTime: 30_000
              });
            }
          }}>
              <span className="flex size-[18px] shrink-0 items-center justify-center">
                <Image src={MigrationWizardSvg} alt="" aria-hidden="true" width={16} height={16} className="size-4" />
              </span>
              {/* whitespace-nowrap keeps the label on one line while the
                  sidebar width is still animating open. */}
              <span className="whitespace-nowrap group-data-[state=collapsed]:hidden">
                Migration Wizard
              </span>
            </button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent side="right" align="center" hidden={sidebarState !== "collapsed" || isMobile}>
          {isPersonalOrg ? "Blacksmith doesn't support personal orgs" : "Migration Wizard"}
        </TooltipContent>
      </Tooltip>
      <DialogContent translate="no" className="notranslate w-[760px] max-w-[760px] max-h-[90vh] overflow-y-auto" onInteractOutside={e_0 => {
      // The repository dropdown popover is portaled to the body, so
      // interactions inside it register as outside the dialog. Don't let
      // those close the wizard (especially on the repository step where
      // canClose is true).
      const target = e_0.detail.originalEvent.target as Element | null;
      if (target?.closest("[data-radix-popper-content-wrapper]")) {
        e_0.preventDefault();
        return;
      }
      if (!canClose) {
        e_0.preventDefault();
      }
    }} onEscapeKeyDown={e_1 => {
      if (!canClose) {
        e_1.preventDefault();
      }
    }}>
        <BSErrorBoundary title="Migration Wizard" FallbackComponent={ErrorFallback}>
          <Suspense fallback={<MigrationWizardSkeleton />}>
            <MigrationWizard />
          </Suspense>
        </BSErrorBoundary>
      </DialogContent>
    </Dialog>;
}